import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getLockedPayments,
  getPayments,
  getActivePaymentLoader,
  getLockedPaymentLoader,
  getAllReceivers,
} from 'app/state/ducks/payments/selectors'
import { paymentCols } from './payments-columnDefs'
import {
  loadActivePayments,
  loadLockedPayments,
  loadReceivers,
} from 'app/state/ducks/payments/thunks'
import { PaymentParams } from 'app/models/payments-params'
import { MRT_ColumnDef } from 'mantine-react-table'
import ReleaseQueueDrawer from '../drawer/drawer'
import { Stack } from '@mantine/core'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import { getKPIData } from './payments-kpi-data'
import MainTable from 'app/views/components/Table/MainTable'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { getEntities } from 'app/state/ducks/entities/selectors'
import {
  GridSortingStorage,
  GridFilterStorage,
  GridPaginationStorage,
  GridColumnVisibilityStorage,
  useGridSorting,
  useGridFilters,
  useGridPagination,
  useGridColumnVisibility,
} from 'app/utils/grid-sorting-storage'

interface props {
  tab?: string | null | undefined
}

export function PaymentsPage({ tab }: props) {
  const [row, setRow] = useState<PaymentParams>()
  const [open, setOpen] = useState<boolean>(false)
  const [columnDefs, setColumnDefs] = useState<MRT_ColumnDef<PaymentParams>[]>(
    [],
  )
  const [payments, setPayments] = useState<PaymentParams[]>([])
  const activePayments: PaymentParams[] = useSelector(getPayments)
  const lockedPayments: PaymentParams[] = useSelector(getLockedPayments)
  const allReceivers = useSelector(getAllReceivers)
  const activePaymentLoader = useSelector(getActivePaymentLoader)
  const lockedPaymentLoader = useSelector(getLockedPaymentLoader)
  const entities = useSelector(getEntities)
  const [globalLoading, setGlobalLoading] = useState(false)
  const dispatch = useDispatch()

  const [filteredPayments, setFilteredPayments] =
    useState<PaymentParams[]>(payments)
  const kpiData = getKPIData(payments, setFilteredPayments)

  // Grid preferences hooks
  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.payments,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.payments,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.payments)
  const { toMRTColumnFilters, handleMRTFiltersChange } = useGridFilters(
    GridFilterStorage.payments,
  )

  useEffect(() => {
    setGlobalLoading(activePaymentLoader || lockedPaymentLoader)
  }, [activePaymentLoader, lockedPaymentLoader])

  // The active grid is ONE request (ADR_LOS-030): the rows arrive with their
  // alert already worked out, so it loads no receiver or entity list. The
  // archived grid is unchanged: it still works its alert out here, from the
  // tenant-wide receiver list and the entity list — the active grid used to
  // load the receivers for it, so now it loads them itself.
  useEffect(() => {
    if (tab === 'archived') {
      dispatch(loadEntities())
      dispatch(loadReceivers())
      dispatch(loadLockedPayments())
    } else {
      dispatch(loadActivePayments())
    }
  }, [tab])

  useEffect(() => {
    if (tab === 'archived') {
      setPayments(lockedPayments)
    } else {
      setPayments(activePayments)
    }
  }, [tab, activePayments, lockedPayments])

  useEffect(() => {
    setColumnDefs(paymentCols())
  }, [])

  function compare(a: PaymentParams, b: PaymentParams) {
    const weight = new Map([
      ['Prepayment', 1],
      ['Repayment', 2],
      ['Notice', 3],
      ['PrincipalPaymentTransaction', 4],
      ['PrincipalWithInterestPaymentTransaction', 5],
      ['InterestPaymentTransaction', 6],
      ['NewBorrowingTransaction', 7],
      ['Facility Fees', 8],
    ])

    const aWeight = weight.get(a.payment.transactionType) ?? 0
    const bWeight = weight.get(b.payment.transactionType) ?? 0
    if (aWeight > bWeight) {
      return 1
    }
    if (aWeight < bWeight) {
      return -1
    }
    return 0
  }

  const cleanPaymentData = () => {
    // The active rows carry the server's alert (the same rule, ported); only
    // the archived grid still works it out here, from the lists it loads.
    if (tab !== 'archived') return filteredPayments
    return filteredPayments.map(a => {
      const paymentReceiver = allReceivers.filter(r => r.paymentId.id === a.id)
      const isWIMissing = paymentReceiver.some(r => !r.wireInstruction)
      const notValidEntity = paymentReceiver.some(payment => {
        const entity = entities.find(
          entity => entity.id === payment.lenderId.id,
        )
        return entity && entity.kycStatus !== 'ValidatedStatus'
      })
      const notValidEntityText = notValidEntity
        ? 'KYC Status needs to be Validated for payment release. '
        : ''
      const isWIMissingText = isWIMissing ? 'Wire Instruction is missing. ' : ''

      return {
        ...a,
        hasAlert: isWIMissing || notValidEntity,
        alertText: notValidEntityText + isWIMissingText,
      }
    })
  }

  useEffect(() => {
    const pay = payments || []
    setFilteredPayments(pay.sort((a, b) => compare(a, b)))
  }, [payments])

  function handleRowClicked(e: any) {
    setRow(e)
    setOpen(true)
  }

  return (
    <Stack>
      <KPI_Section kpiData={kpiData} isLoading={globalLoading} />
      {columnDefs.length != 0 ? (
        <MainTable
          tableName="Payments Table"
          columnDefs={columnDefs}
          data={cleanPaymentData()}
          initialSorting={[
            { id: sorting.sortBy, desc: sorting.order === 'desc' },
          ]}
          onSortingChange={handleSortingChange}
          initialColumnFilters={toMRTColumnFilters()}
          onColumnFiltersChange={handleMRTFiltersChange}
          initialPagination={pagination}
          onPaginationChange={handlePaginationChange}
          initialColumnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          setRow={handleRowClicked}
          isLoading={globalLoading}
          headerBackgroundColor="#F0EEEE"
          enableTopToolbar={false}
          withPagination={true}
          csvExportEnabled={true}
          enableColumnResizing={false}
        />
      ) : null}
      <ReleaseQueueDrawer open={open} setOpen={setOpen} row={row} />
    </Stack>
  )
}
